<script setup lang="ts">
import StatusPill from '../components/StatusPill.vue'
import { useFormattedDate } from '../composables/useFormattedDate'
import { useNow } from '../composables/useNow'
import type { CvVersion, CvVersionStatus } from '../stores/cv_versions'

// One row per generated CV version, newest first. Selecting a row is the
// parent's concern; this list only reports the click.
defineProps<{
  versions: CvVersion[]
  selectedId: number | null
}>()

const emit = defineEmits<{ select: [id: number] }>()

const { formatDate, formatAbsoluteDate } = useFormattedDate()
const now = useNow()

// Draft = waiting on you (blue), approved = the live one (mint),
// superseded = archived (neutral). The word is always shown too.
const STATUS_CLASS: Record<CvVersionStatus, string> = {
  draft: 'bg-attention/15 text-attention font-semibold capitalize',
  approved: 'bg-brand-primary/15 text-brand-primary font-semibold capitalize',
  superseded: 'bg-border-divider text-ink-muted font-semibold capitalize',
}

function tokens(v: CvVersion): string {
  if (v.input_tokens == null || v.output_tokens == null) return ''
  return `${v.input_tokens.toLocaleString()} in / ${v.output_tokens.toLocaleString()} out`
}
</script>

<template>
  <ul class="m-0 flex list-none flex-col gap-1 p-0" data-testid="cv-version-list">
    <li v-for="v in versions" :key="v.id">
      <button
        type="button"
        class="flex min-h-11 w-full flex-col gap-0.5 rounded-lg px-2 py-1.5 text-left transition-colors"
        :class="v.id === selectedId ? 'bg-surface-hover' : 'hover:bg-surface-hover/60'"
        :data-testid="`cv-version-${v.id}`"
        :aria-pressed="v.id === selectedId"
        @click="emit('select', v.id)"
      >
        <span class="flex w-full items-center gap-2">
          <span class="flex-1 text-sm text-ink">
            <span class="font-medium">v{{ v.id }}</span>
            <span v-if="v.generated_at" class="text-ink-muted" :title="formatAbsoluteDate(v.generated_at)">
              · {{ formatDate(v.generated_at, now) }}</span
            >
          </span>
          <StatusPill
            :label="v.status"
            :color-class="STATUS_CLASS[v.status]"
            :data-testid="`cv-version-status-${v.id}`"
          />
        </span>
        <span class="flex flex-wrap gap-x-2 text-xs text-ink-faint">
          <span v-if="v.model" class="font-mono">{{ v.model }}</span>
          <span v-if="tokens(v)">{{ tokens(v) }}</span>
          <span>{{ v.citations.length }} source {{ v.citations.length === 1 ? 'page' : 'pages' }}</span>
        </span>
      </button>
    </li>
  </ul>
</template>
